import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Phone, CheckCircle, XCircle, Users, Clock, MapTrifold, ChartBar, Buildings } from "@phosphor-icons/react";
import { useSeo } from "@/lib/useSeo";
import QuoteRequestForm from "@/components/QuoteRequestForm";
import { MobileActionBar } from "@/components/MobileActionBar";
import { SiteHeader, PHONE_DISPLAY, PHONE_HREF } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";

const NAVY = "#182848";
const ORANGE = "#EE7623";
const SLATE = "#4A5A72";
const CREAM = "#FDF3EA";
const WHITE = "#FFFFFF";
const LINE = "rgba(24,40,72,0.14)";

export const PROPERTY_MANAGERS_SEO = {
  title: "Property Manager Services Baltimore, MD | One Vendor | HCOB",
  description: "Turnovers, eviction cleanouts and multi-unit programs across Baltimore and Maryland, run as one managed job under one point of contact. Set up a property account.",
  path: "/property-managers",
  og: {
    "og:title": "For Property Managers | HCOB Property Services",
    "og:site_name": "HCOB Property Services",
    "og:description": "Clear it, clean it, repair it, hand it back rent-ready — under one point of contact. Baltimore and all of Maryland.",
    "og:url": "https://hcobnetwork.com/property-managers",
    "og:type": "website",
  },
};

const NAV_ANCHORS = [
  { href: "#process", label: "How it works" },
  { href: "#comparison", label: "Why HCOB" },
  { href: "#quote", label: "Set Up an Account", primary: true },
];

const STEPS = [
  { n: "01", title: "Tell Us the Project", body: "One conversation covers everything — whether it's a single deep clean or a full multi-trade renovation. We scope it, price it, and hand you one clear quote." },
  { n: "02", title: "We Dispatch Vetted Pros", body: "Every professional in our network is vetted and managed by our operations team. Crews clock in with GPS verification, work from a custom checklist built for your exact job, and answer to us — not to chance." },
  { n: "03", title: "One Team Manages It Start to Finish", body: "We sequence the trades, coordinate the schedules, handle the crews, and keep you updated through one point of contact. You review the finished work. That's your whole job." },
];

const COMPARISON_ROWS = [
  { dim: "Professionals", hcob: "Vetted, managed local crews", leadGen: "Anyone who paid for a listing" },
  { dim: "Coordination", hcob: "One point of contact for the entire project", leadGen: "You chase 4 different strangers" },
  { dim: "Project Management", hcob: "Multi-trade projects sequenced and coordinated for you", leadGen: "One trade at a time — you are the project manager" },
  { dim: "Accountability", hcob: "GPS-verified clock-in and platform-tracked work", leadGen: "Hope they show up" },
  { dim: "Billing", hcob: "One quote, one invoice, one accountable team", leadGen: "4 quotes, 4 contracts, 4 chances for surprises" },
  { dim: "Problem Resolution", hcob: "Problems fixed by us — accountability is built in", leadGen: "Disputes are between you and the contractor" },
];

const CASE_METRICS = [
  { icon: Users, k: "2×", label: "Workforce doubled without adding management overhead" },
  { icon: Clock, k: "2 shifts", label: "Two-shift model — daily output capacity doubled" },
  { icon: MapTrifold, k: "GPS", label: "Verified clock-in through the HCOB Property Services app" },
  { icon: ChartBar, k: "Live", label: "Automated quota tracking and payroll on-platform" },
];

const fadeUp = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } } };
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } } };

const Eyebrow = ({ children, light }) => (
  <div className="font-mono-label text-[10px] tracking-[0.28em]" style={{ color: light ? "rgba(255,255,255,0.7)" : ORANGE }}>{children}</div>
);
const H2 = ({ children, testid, light }) => (
  <h2 className="font-display mt-4 font-black leading-[1.02] tracking-tighter" style={{ color: light ? WHITE : NAVY, fontSize: "clamp(30px, 4vw, 52px)" }} data-testid={testid}>{children}</h2>
);

export default function PropertyManagers() {
  useSeo(PROPERTY_MANAGERS_SEO);
  return (
    <div data-testid="property-managers-page" className="min-h-screen antialiased" style={{ backgroundColor: WHITE, color: NAVY }}>
      <SiteHeader anchors={NAV_ANCHORS} brandTestId="pm-brand" />

      {/* ── Hero ── */}
      <section id="top" data-testid="pm-hero" style={{ backgroundColor: NAVY, color: WHITE }}>
        <motion.div initial="hidden" animate="show" variants={stagger} className="mx-auto grid max-w-[1400px] grid-cols-1 gap-10 px-5 py-14 md:px-10 md:py-16 lg:grid-cols-12 lg:items-end lg:py-20">
          <div className="lg:col-span-8">
          <motion.div variants={fadeUp} className="font-mono-label flex items-center gap-2 text-[10px] tracking-[0.26em] text-white/70">
            <Buildings size={14} weight="duotone" style={{ color: ORANGE }} /> For property managers · Baltimore &amp; all of Maryland
          </motion.div>
          <motion.h1 data-testid="pm-h1" variants={fadeUp} className="font-display mt-8 max-w-[16ch] text-4xl font-black leading-[0.98] tracking-tighter sm:text-5xl lg:text-6xl">
            Turnovers, Cleanouts and Repairs — <span style={{ color: ORANGE }}>Handled by One Team</span>
          </motion.h1>
          <motion.p variants={fadeUp} className="mt-7 max-w-[660px] text-[15px] leading-[1.65] text-white/80 md:text-base" data-testid="pm-sub">
            If you manage units, property work isn't a one-time problem. It's every turnover, every eviction, every tenant who left the couch behind. We run the whole job — clear it, clean it, repair it, hand it back rent-ready — under one point of contact.
          </motion.p>
          <motion.div variants={fadeUp} className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center">
            <a href="#quote" data-testid="pm-hero-cta" className="inline-flex h-14 items-center justify-center gap-2 rounded-sm px-7 text-[14px] font-black text-white" style={{ backgroundColor: ORANGE }}>
              Set Up a Property Account <ArrowRight size={16} weight="bold" />
            </a>
            <a href={PHONE_HREF} data-testid="pm-hero-call" className="inline-flex h-14 items-center justify-center gap-2 rounded-sm border border-white/30 px-6 text-[13px] font-bold text-white hover:bg-white/10">
              <Phone size={16} weight="fill" /> Call or Text {PHONE_DISPLAY}
            </a>
          </motion.div>
          <motion.p variants={fadeUp} className="mt-6 text-[14px] text-white/75" data-testid="pm-turnover-line">
            Need a single unit turned now? <a href="/landlord-turnover" data-testid="pm-turnover-link" className="font-bold underline underline-offset-4" style={{ color: ORANGE }}>See eviction cleanouts and rental turnovers.</a>
            {" "}Need it today? <a href="/same-day-cleaning" data-testid="need-it-today" className="font-bold underline underline-offset-4" style={{ color: ORANGE }}>Same-day cleaning in Baltimore — request by noon.</a>
          </motion.p>
          </div>
          <motion.div variants={fadeUp} className="grid grid-cols-3 gap-6 border-t border-white/15 pt-8 lg:col-span-4 lg:grid-cols-1 lg:gap-7 lg:border-l lg:border-t-0 lg:pl-10 lg:pt-0" data-testid="pm-stat-row">
            {[["1", "Point of contact per portfolio"], ["13+", "Trades on call"], ["MD", "Statewide coverage"]].map(([k, l]) => (
              <div key={l}>
                <div className="font-display text-3xl font-black leading-none tracking-tight lg:text-4xl" style={{ color: ORANGE }}>{k}</div>
                <div className="font-mono-label mt-2 text-[10px] tracking-[0.22em] text-white/80">{l}</div>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </section>

      {/* ── Problem ── */}
      <section data-testid="pm-problem" className="border-b" style={{ backgroundColor: WHITE, borderColor: LINE }}>
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-10 px-5 py-14 md:grid-cols-12 md:px-10 md:py-24">
          <div className="md:col-span-5 md:sticky md:top-24 md:self-start">
            <Eyebrow>01 · The problem</Eyebrow>
            <H2>Managing contractors shouldn't be a <span style={{ color: ORANGE }}>second job.</span></H2>
          </div>
          <div className="md:col-span-7">
            <p className="font-display text-[22px] leading-[1.5] tracking-tight md:text-[26px]" style={{ color: NAVY }}>
              <span className="float-left mr-3 mt-1 font-display text-[72px] font-black leading-[0.8]" style={{ color: ORANGE }} aria-hidden>“</span>
              Finding reliable help means searching, calling, comparing quotes, and hoping the person who shows up is the person you vetted.
            </p>
            <div className="mt-6 space-y-5 text-[15px] leading-[1.75] md:text-base" style={{ color: SLATE }}>
              <p>Multiply that by every trade your property needs, and you've become an unpaid project manager. One no-show throws off the whole timeline. One miscommunication means paying twice for the same work.</p>
              <p>And when something goes wrong, every contractor points at the other one.</p>
              <p className="font-display text-lg font-bold" style={{ color: NAVY }}>There's a better way to get things done.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Three steps ── */}
      <section id="process" data-testid="pm-process" className="border-b" style={{ backgroundColor: CREAM, borderColor: LINE }}>
        <div className="mx-auto max-w-[1400px] px-5 py-14 md:px-10 md:py-24">
          <Eyebrow>02 · How the network works</Eyebrow>
          <H2>Three steps. Zero contractor juggling.</H2>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={stagger} className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
            {STEPS.map((s, i) => (
              <motion.div key={s.n} data-testid={`process-step-${s.n}`} variants={fadeUp} className="flex flex-col justify-between rounded-sm border bg-white p-7 transition-transform hover:-translate-y-1" style={{ borderColor: LINE }}>
                <div>
                  <div className="font-display text-[68px] font-black leading-none tracking-tighter" style={{ color: ORANGE }}>{s.n}</div>
                  <div className="font-display mt-6 text-xl font-black tracking-tight" style={{ color: NAVY }}>{s.title}</div>
                  <p className="mt-3 text-[14px] leading-[1.65]" style={{ color: SLATE }}>{s.body}</p>
                </div>
                {i < STEPS.length - 1 && <div className="mt-6 hidden items-center gap-2 font-mono-label text-[10px] tracking-[0.28em] md:flex" style={{ color: SLATE }}>NEXT <ArrowRight size={12} /></div>}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Multi-trade narrative ── */}
      <section data-testid="pm-multi-trade" className="border-b" style={{ backgroundColor: WHITE, borderColor: LINE }}>
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-10 px-5 py-14 md:grid-cols-12 md:px-10 md:py-24">
          <div className="md:col-span-5">
            <div className="rounded-sm p-8 text-white" style={{ backgroundColor: NAVY }}>
              <div className="font-mono-label text-[9px] tracking-[0.26em]" style={{ color: ORANGE }}>One outcome</div>
              <div className="font-display mt-3 text-[26px] font-black leading-tight tracking-tight">You don't hire four contractors — you hire one outcome.</div>
              <div className="mt-8 grid grid-cols-2 gap-6 border-t border-white/15 pt-6">
                {[["1", "Quote"], ["1", "Invoice"], ["1", "Point of contact"], ["13+", "Trades on call"]].map(([k, l]) => (
                  <div key={l}><div className="font-display text-3xl font-black leading-none" style={{ color: ORANGE }}>{k}</div><div className="font-mono-label mt-2 text-[10px] tracking-[0.22em] text-white/65">{l}</div></div>
                ))}
              </div>
            </div>
          </div>
          <div className="md:col-span-7">
            <Eyebrow>03 · Multi-trade projects</Eyebrow>
            <H2>One project. Every trade. <span style={{ color: ORANGE }}>One point of accountability.</span></H2>
            <div className="mt-8 space-y-5 text-[15px] leading-[1.75] md:text-base" style={{ color: SLATE }}>
              <p>Say a unit needs a new roof, window installation, carpet cleaning, and someone to handle the admin work behind it all. On a lead-gen site, that's four separate searches, four vetting processes, four contracts — and you personally coordinating the sequence so the roofer finishes before the window crew arrives and the carpets get cleaned last.</p>
              <p className="border-l-2 pl-4" style={{ borderColor: ORANGE, color: NAVY }}>If one contractor flakes, your whole timeline collapses, and nobody is accountable.</p>
              <p>With HCOB Property Services, it's one call. We scope the full project, sequence every trade in the right order, manage every crew on site, and deliver a finished project — with <span className="font-semibold" style={{ color: NAVY }}>better combined value than piecing it together yourself</span>, because one coordinated team means no overlap costs, no re-dos, and no paying twice for miscommunication.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Comparison ledger ── */}
      <section id="comparison" data-testid="pm-comparison" className="border-b" style={{ backgroundColor: CREAM, borderColor: LINE }}>
        <div className="mx-auto max-w-[1400px] px-5 py-14 md:px-10 md:py-24">
          <Eyebrow>04 · The comparison</Eyebrow>
          <H2>HCOB Property Services vs. Lead-Gen Sites</H2>
          <p className="mt-4 text-[16px] leading-relaxed" style={{ color: SLATE }}>Lead-gen sites sell you contacts. <span className="font-display font-bold" style={{ color: ORANGE }}>We deliver outcomes.</span></p>
          <div className="mt-12 overflow-hidden rounded-sm border bg-white" style={{ borderColor: LINE }}>
            <div className="grid grid-cols-12 border-b" style={{ borderColor: LINE }}>
              <div className="col-span-3 px-4 py-4 font-mono-label text-[10px] tracking-[0.26em] md:px-8" style={{ color: SLATE }}>Dimension</div>
              <div className="col-span-5 px-4 py-4 text-white md:px-8" style={{ backgroundColor: NAVY }}>
                <div className="font-mono-label text-[10px] tracking-[0.26em] text-white/70">HCOB</div>
                <div className="mt-0.5 font-display text-[13px] font-bold">Managed outcome</div>
              </div>
              <div className="col-span-4 px-4 py-4 font-mono-label text-[10px] tracking-[0.26em] md:px-8" style={{ color: SLATE }}>
                Lead-gen sites
                <div className="mt-0.5 font-display text-[13px] font-bold normal-case tracking-normal">(Angi, Thumbtack, Facebook)</div>
              </div>
            </div>
            {COMPARISON_ROWS.map((r, i) => (
              <div key={r.dim} data-testid={`comparison-row-${r.dim.toLowerCase().replace(/\s+/g, "-")}`} className="grid grid-cols-12" style={{ borderTop: i ? `1px solid ${LINE}` : "none" }}>
                <div className="col-span-3 px-4 py-6 md:px-8"><div className="font-display text-[14px] font-bold tracking-tight md:text-[15px]" style={{ color: NAVY }}>{r.dim}</div></div>
                <div className="col-span-5 flex items-start gap-3 px-4 py-6 md:px-8" style={{ backgroundColor: "rgba(24,40,72,0.05)" }}>
                  <CheckCircle size={18} weight="fill" className="mt-[3px] shrink-0" style={{ color: NAVY }} />
                  <div className="text-[13px] leading-[1.55] md:text-[15px]" style={{ color: NAVY }}>{r.hcob}</div>
                </div>
                <div className="col-span-4 flex items-start gap-3 px-4 py-6 md:px-8">
                  <XCircle size={18} weight="fill" className="mt-[3px] shrink-0" style={{ color: "rgba(74,90,114,0.5)" }} />
                  <div className="text-[13px] leading-[1.55] md:text-[14px]" style={{ color: SLATE }}>{r.leadGen}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Case study ── */}
      <section id="case-study" data-testid="pm-case-study" className="relative overflow-hidden" style={{ backgroundColor: NAVY, color: WHITE }}>
        <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(#FFFFFF 1px, transparent 1px)", backgroundSize: "22px 22px" }} />
        <div className="relative mx-auto grid max-w-[1400px] grid-cols-1 gap-12 px-5 py-16 md:grid-cols-12 md:px-10 md:py-24">
          <div className="md:col-span-7">
            <Eyebrow light>05 · Case study</Eyebrow>
            <H2 light>A recycling operation that runs <span style={{ color: ORANGE }}>without the owner on site.</span></H2>
            <div className="mt-8 space-y-5 text-[15px] leading-[1.75] text-white/80 md:text-base">
              <p>A Baltimore-area recycling operation depended entirely on one person being physically on site every day. No tracking, no accountability system, no way to scale.</p>
              <p>HCOB Property Services replaced that dependency with full <span className="text-white">virtual management infrastructure</span>: a dedicated operations coordinator, a two-shift model that doubled daily output capacity, GPS-verified clock-in through the HCOB app, automated quota tracking, and payroll managed entirely on the platform.</p>
              <p className="border-l-2 py-3 pl-4 font-display text-lg font-bold" style={{ borderColor: ORANGE, backgroundColor: "rgba(255,255,255,0.05)" }}>We don't just send workers. We install the system.</p>
            </div>
          </div>
          <div className="md:col-span-5">
            <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} variants={stagger} className="grid grid-cols-2 gap-4">
              {CASE_METRICS.map((m) => {
                const Icon = m.icon;
                return (
                  <motion.div key={m.label} data-testid={`case-study-metric-${m.k.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} variants={fadeUp} className="rounded-sm border border-white/15 p-5" style={{ backgroundColor: "rgba(255,255,255,0.04)" }}>
                    <Icon size={20} weight="duotone" style={{ color: ORANGE }} />
                    <div className="font-display mt-3 text-3xl font-black leading-none tracking-tighter">{m.k}</div>
                    <div className="mt-2 text-[11px] leading-snug text-white/70">{m.label}</div>
                  </motion.div>
                );
              })}
            </motion.div>
            <div className="mt-4 font-mono-label text-[9px] tracking-[0.28em] text-white/50">Baltimore, MD · Live client</div>
          </div>
        </div>
      </section>

      {/* ── Set up a property account (quote form) ── */}
      <section id="quote" data-testid="pm-quote" style={{ backgroundColor: WHITE }}>
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-10 px-5 py-14 md:grid-cols-12 md:px-10 md:py-24">
          <div className="md:col-span-5">
            <Eyebrow>06 · Set up a property account</Eyebrow>
            <H2>Tell us about your portfolio.</H2>
            <p className="mt-6 text-[15px] leading-relaxed" style={{ color: SLATE }}>
              Number of units, what turns over most, and what's eating your time. A coordinator will call to set up pricing, a turnover checklist and a single point of contact for every unit.
            </p>
            <ul className="mt-8 space-y-4">
              {["Multi-unit and recurring programs", "One invoice per job, one contact for all trades", "Rent-ready hand-back on every turnover"].map((t) => (
                <li key={t} className="flex items-start gap-3 text-[14px]" style={{ color: NAVY }}>
                  <span className="mt-[9px] inline-block h-[6px] w-8 shrink-0" style={{ backgroundColor: ORANGE }} />{t}
                </li>
              ))}
            </ul>
          </div>
          <div className="order-first md:order-none md:col-span-7">
            <div className="rounded-sm border bg-white p-4 md:p-6" style={{ borderColor: LINE }}>
              <QuoteRequestForm />
            </div>
          </div>
        </div>
      </section>

      <SiteFooter quoteHref="#quote" />
      <MobileActionBar primaryLabel="Set Up a Property Account" primaryHref="#quote" phoneHref={PHONE_HREF} textBody="Hi HCOB Property Services — I manage properties and want to set up an account. " watch={["#top", "#quote"]} accent={ORANGE} testid="pm-mobile-bar" />
    </div>
  );
}
